import { supabase } from '@/config/supabase';

const API_BASE_URL = import.meta.env.VITE_PRICE_INTEL_API_BASE_URL;

if (!API_BASE_URL) {
  console.warn('VITE_PRICE_INTEL_API_BASE_URL is not set. Price intel module requests will fail.');
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  const token = data.session?.access_token;
  if (!token) throw new Error('No authenticated session found for price intel API');
  return token;
}

export interface PriceIntelRequestOptions extends RequestInit {
  /** 跳过 Supabase 鉴权（用于公开解析接口、健康检查等）。 */
  skipAuth?: boolean;
  /** 传入自定义 body 类型时，不自动加 Content-Type: application/json（比如 FormData）。 */
  jsonBody?: boolean;
}

export async function priceIntelRequest<T>(
  path: string,
  init?: PriceIntelRequestOptions,
): Promise<T> {
  if (!API_BASE_URL) {
    throw new Error('VITE_PRICE_INTEL_API_BASE_URL is not configured');
  }

  const { skipAuth, jsonBody, headers, ...rest } = init || {};
  const mergedHeaders: Record<string, string> = {
    ...(jsonBody !== false ? { 'Content-Type': 'application/json' } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  if (!skipAuth) {
    const token = await getAccessToken();
    mergedHeaders.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers: mergedHeaders,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Price intel API request failed with ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/** 供 FormData 上传使用：浏览器会自动带上 multipart boundary，不能人为设 Content-Type。 */
export async function priceIntelMultipartRequest<T>(
  path: string,
  formData: FormData,
  options: { skipAuth?: boolean } = {},
): Promise<T> {
  if (!API_BASE_URL) {
    throw new Error('VITE_PRICE_INTEL_API_BASE_URL is not configured');
  }

  const headers: Record<string, string> = {};
  if (!options.skipAuth) {
    const token = await getAccessToken();
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Price intel API request failed with ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

